import { useState, useRef, useEffect, useMemo } from "react";
import "../../css/Components/Sidebar/Sidebar.css";
import { useNavigate, useLocation } from "react-router-dom";
import { signOut } from "firebase/auth";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUserGroup } from "@fortawesome/free-solid-svg-icons";

import { auth } from "../../backend/firebaseconfig";

import LogoutButton from "./Buttons/LogoutButton";
import { SwitchAdviserModal } from "./Admin/SwitchAdviserModal";

import { useUserRole } from "../../context/UserRoleContext";
import { useUserEmail } from "../../context/UserEmailContext";
import { useUserPermissions } from "../../context/UserPermissionsContext";
import { useAdviserStatus } from "../../context/AdviserStatusContext";
import { useAdminStatus } from "../../context/AdminStatusContext";
import { useAdminAdviserMails } from "../../context/AdminAdviserEmailsContext";
import { getFirstNameFromUser } from "../../context/HelperFunctions/getPreferredGreetingName";

import { canAccessAML } from "../Tools/AML/Utils/permissions";
import { getPortalSectionConfig } from "../PortalSections/utils/portalSectionConfig";

import { ReactComponent as Logo } from "../../assets/icons/logo-Ghost-White.svg";
import { ReactComponent as IconExit } from "../../assets/icons/Icon-Exit.svg";
import { ReactComponent as IconExpand } from "../../assets/icons/icon-Expand.svg";
import { ReactComponent as IconCollapse } from "../../assets/icons/icon-Collapse.svg";

const safeParseArray = (value) => {
  try {
    const parsed = JSON.parse(value || "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const Sidebar = ({ shouldNavigate, isSetExpanded = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const overlayRef = useRef(null);

  const { userRole } = useUserRole();
  const { userEmail, setUserEmail } = useUserEmail();
  const { userPermissions = [] } = useUserPermissions();
  const { adviserStatus = [] } = useAdviserStatus();
  const { adminStatus = [] } = useAdminStatus();
  const { currentAdviserEmail } = useAdminAdviserMails();

  const [isOverlayVisible, setOverlayVisible] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(isSetExpanded);
  const [canSeeAML, setCanSeeAML] = useState(false);
  const [preferredGreetingName, setPreferredGreetingName] = useState("");

  const [adminAdviserMails, setAdminAdviserMails] = useState(() => {
    return safeParseArray(localStorage.getItem("adminAdviserMails"));
  });

  const effectiveUserPermissions = useMemo(() => {
    const safeUserPermissions = Array.isArray(userPermissions)
      ? userPermissions
      : [];

    if (userRole !== "External") {
      return safeUserPermissions;
    }

    const externalPermissions = safeParseArray(
      localStorage.getItem("externalPermissions"),
    );

    return Array.from(
      new Set([...safeUserPermissions, ...externalPermissions]),
    );
  }, [userRole, userPermissions]);

  useEffect(() => {
    let mounted = true;

    const checkAccess = async () => {
      const allowed = await canAccessAML({
        userRole,
        userPermissions: effectiveUserPermissions,
        userEmail,
      });

      if (mounted) {
        setCanSeeAML(allowed);
      }
    };

    checkAccess();

    return () => {
      mounted = false;
    };
  }, [userRole, effectiveUserPermissions, userEmail]);

  useEffect(() => {
    if (!currentAdviserEmail) return;
  }, [currentAdviserEmail]);

  useEffect(() => {
    let mounted = true;

    const loadPreferredGreetingName = async () => {
      const name = await getFirstNameFromUser(auth.currentUser || {});

      if (mounted) {
        setPreferredGreetingName(name || "");
      }
    };

    loadPreferredGreetingName();

    return () => {
      mounted = false;
    };
  }, [auth.currentUser?.uid, auth.currentUser?.email, auth.currentUser?.role]);

  const navigationItems = useMemo(() => {
    const portalSectionConfig = getPortalSectionConfig({
      userRole,
      userPermissions: effectiveUserPermissions,
      adviserStatus,
      adminStatus,
      canSeeAML,
    });

    return Object.values(portalSectionConfig)
      .filter((section) => section.show)
      .map((section) => ({
        label: section.title,
        route: section.route,
        icn: section.navIcon,
        responsiceIcon: section.responsiveIcon,
        matchRoutes: section.matchRoutes,
        visible: true,
      }));
  }, [
    userRole,
    effectiveUserPermissions,
    adviserStatus,
    adminStatus,
    canSeeAML,
  ]);

  const isActiveRoute = (item) => {
    const currentPath = String(location.pathname || "").toLowerCase();
    let decodedCurrentPath = currentPath;

    try {
      decodedCurrentPath = decodeURIComponent(currentPath).toLowerCase();
    } catch {
      decodedCurrentPath = currentPath;
    }

    return item.matchRoutes?.some((route) => {
      const normalisedRoute = route.toLowerCase();

      return (
        currentPath === normalisedRoute ||
        decodedCurrentPath === normalisedRoute ||
        currentPath.startsWith(`${normalisedRoute}/`) ||
        decodedCurrentPath.startsWith(`${normalisedRoute}/`)
      );
    });
  };

  const handleItemClick = (item) => {
    if (!shouldNavigate || shouldNavigate(item.route)) {
      navigate(item.route);
    }
  };

  const clearAppCache = async () => {
    localStorage.clear();
    sessionStorage.clear();

    if ("caches" in window) {
      const cacheNames = await caches.keys();
      await Promise.all(
        cacheNames.map((cacheName) => caches.delete(cacheName)),
      );
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);

      await clearAppCache();

      setAdminAdviserMails([]);
      setCanSeeAML(false);
      setIsModalOpen(false);
      setOverlayVisible(false);
      setUserEmail("");

      navigate("/", { replace: true });
    } catch (error) {
      console.error("Error during logout:", error);

      await clearAppCache();

      setAdminAdviserMails([]);
      setCanSeeAML(false);
      setIsModalOpen(false);
      setOverlayVisible(false);
      setUserEmail("");

      navigate("/", { replace: true });
    }
  };

  const handleClickOutside = (event) => {
    if (
      isOverlayVisible &&
      overlayRef.current &&
      !overlayRef.current.contains(event.target)
    ) {
      setOverlayVisible(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOverlayVisible]);

  useEffect(() => {
    const storedMails = localStorage.getItem("adminAdviserMails");
    const storedUserEmail = localStorage.getItem("userEmail");

    if (storedMails) {
      setAdminAdviserMails(safeParseArray(storedMails));
    }

    if (storedUserEmail) {
      setUserEmail(storedUserEmail);
    }
  }, [setUserEmail]);

  useEffect(() => {
    if (userEmail) {
      localStorage.setItem("userEmail", userEmail);
    }
  }, [userEmail]);

  useEffect(() => {
    if (userRole) {
      localStorage.setItem("userRole", userRole);
    }
  }, [userRole]);

  useEffect(() => {
    if (adminAdviserMails.length > 0) {
      localStorage.setItem(
        "adminAdviserMails",
        JSON.stringify(adminAdviserMails),
      );
    }
  }, [adminAdviserMails]);

  useEffect(() => {
    if (adviserStatus.length > 0) {
      localStorage.setItem("adviserStatus", JSON.stringify(adviserStatus));
    }
  }, [adviserStatus]);

  useEffect(() => {
    if (adminStatus.length > 0) {
      localStorage.setItem("adminStatus", JSON.stringify(adminStatus));
    }
  }, [adminStatus]);

  return (
    <>
      <div className={`sidebar ${isExpanded ? "expanded" : ""}`}>
        <div className={`sidebar-content ${isExpanded ? "expanded" : ""}`}>
          <div className="sidebar-expanded">
            <IconExpand onClick={() => setIsExpanded(!isExpanded)} />
          </div>

          <div className="expandIcon">
            <IconCollapse onClick={() => setIsExpanded(!isExpanded)} />
          </div>

          <div className="sidebar-logo">
            <Logo
              onClick={() => {
                window.open("https://rosemountfs-ifa.co.uk/", "_blank");
              }}
            />
          </div>

          {navigationItems.map((item) => (
            <div
              className={`sidebar-Tab ${isActiveRoute(item) ? "active" : ""}`}
              key={item.label}
              onClick={() => handleItemClick(item)}
            >
              <div className="sidebar-defalut-icon">
                <item.icn />
              </div>

              <div className="sidebar-responsive-icon">
                <item.responsiceIcon />
              </div>

              <div className="nav-item Title">{item.label}</div>
            </div>
          ))}
        </div>

        <IconExit className="exit-icon" onClick={handleLogout} />

        <div className="sidebar-footer">
          {adminAdviserMails.length > 1 && (
            <FontAwesomeIcon
              className="SideBarFooterIcon"
              icon={faUserGroup}
              title="Click switch adviser"
              onClick={() => setIsModalOpen(true)}
            />
          )}

          <div className="Label">{preferredGreetingName}</div>

          <LogoutButton title="Logout" onClick={handleLogout} />
        </div>
      </div>

      {isModalOpen && (
        <SwitchAdviserModal
          isModalOpen={isModalOpen}
          setIsModalOpen={setIsModalOpen}
          onConfirm={(email) => {
            setUserEmail(email);
            setIsModalOpen(false);
          }}
        />
      )}

      <div className={`sidebar-toggle ${isExpanded ? "open" : "closed"}`}>
        <div className="sidebar-toggle-column">
          <div>
            <IconCollapse onClick={() => setIsExpanded(false)} />

            <div className="sidebar-toggle-logo">
              <Logo
                onClick={() => {
                  window.open("https://rosemountfs-ifa.co.uk/", "_blank");
                }}
              />
            </div>
          </div>

          {navigationItems.map((item) => (
            <div
              className={`sidebar-Tab ${isActiveRoute(item) ? "active" : ""}`}
              key={item.label}
              onClick={() => handleItemClick(item)}
            >
              <div className="sidebar-toggle-icon">
                <item.icn />
              </div>

              <div className="Title">{item.label}</div>
            </div>
          ))}
        </div>

        <div className="sidebar-toggle-footer">
          {adminAdviserMails.length > 1 && (
            <FontAwesomeIcon
              className="SideBarFooterIcon"
              icon={faUserGroup}
              title="Click switch adviser"
              onClick={() => setIsModalOpen(true)}
            />
          )}

          <div className="Label">{preferredGreetingName}</div>

          <LogoutButton title="Logout" onClick={handleLogout} />
        </div>
      </div>
    </>
  );
};

export default Sidebar;
